import type { ConfigureFamily, ConfigureOptionDto } from "@/api/configure-options";
import { parseDecimalString } from "@/utils/cost-display";

import { CUSTOM_TOOLING_PART_NO } from "@/utils/configure-storage";

/** POC `renderConfigOptions` category order. */
export const CONFIG_CATEGORY_ORDER = [
  "Profil InDie Options",
  "Machine",
  "Machine Options",
  "Service",
  "Tooling",
  "MDS Options",
  "Sherex Options",
] as const;

export function normUpper(value: string | null | undefined): string {
  return String(value ?? "").trim().toUpperCase();
}

/**
 * POC `getRegularMachineBaseOptions` excludes MO rows with category Tooling;
 * PEM tooling is rendered only in the grouped Tooling section (+ custom row).
 */
export function catalogItemsForFamily(
  items: ReadonlyArray<ConfigureOptionDto>,
  family: ConfigureFamily | null,
): ConfigureOptionDto[] {
  if (family === "heger_pem") {
    return items.filter((o) => normUpper(o.category) !== "TOOLING");
  }
  return [...items];
}

export function getCustomToolingItem(): ConfigureOptionDto {
  return {
    partNo: CUSTOM_TOOLING_PART_NO,
    itemName: "Custom Tooling",
    description: "Custom Tooling",
    category: "Machine Options",
    subCategory: "Engineering",
    price: "0",
    kind: "custom",
  };
}

export function sortCatalogGroups(groups: CatalogGroup[]): CatalogGroup[] {
  const orderIndex = (cat: string) => {
    const i = CONFIG_CATEGORY_ORDER.indexOf(
      cat as (typeof CONFIG_CATEGORY_ORDER)[number],
    );
    return i === -1 ? CONFIG_CATEGORY_ORDER.length + 1 : i;
  };
  return [...groups].sort((a, b) => {
    const ia = orderIndex(a.category);
    const ib = orderIndex(b.category);
    if (ia !== ib) return ia - ib;
    return a.category.localeCompare(b.category);
  });
}

export interface CatalogGroup {
  category: string;
  subgroups: Array<{
    subCategory: string | null;
    items: ConfigureOptionDto[];
  }>;
}

export function buildCatalogGroups(
  items: ReadonlyArray<ConfigureOptionDto>,
): CatalogGroup[] {
  const byCategory = new Map<string, Map<string | null, ConfigureOptionDto[]>>();
  for (const item of items) {
    const cat = item.category ?? "Other";
    const sub = item.subCategory;
    const subMap = byCategory.get(cat) ?? new Map();
    const list = subMap.get(sub) ?? [];
    list.push(item);
    subMap.set(sub, list);
    byCategory.set(cat, subMap);
  }

  const groups = Array.from(byCategory.entries()).map(([category, subMap]) => {
    const subKeys = Array.from(subMap.keys()).sort((a, b) => {
      const maxPrice = (key: string | null) => {
        const arr = subMap.get(key) ?? [];
        let m = -Infinity;
        for (const o of arr) {
          const p = parseDecimalString(o.price);
          if (p != null && p > m) m = p;
        }
        return m;
      };
      const pa = maxPrice(a);
      const pb = maxPrice(b);
      if (pa !== pb) return pb - pa;
      return String(a ?? "").localeCompare(String(b ?? ""));
    });
    return {
      category,
      subgroups: subKeys.map((subCategory) => ({
        subCategory,
        items: sortOptionRows(subMap.get(subCategory) ?? []),
      })),
    };
  });

  return sortCatalogGroups(groups);
}

/**
 * Stable option-row order: price desc, then title.
 *
 * NOTE: intentionally does NOT float selected rows to the top. The POC
 * (`renderConfigOptions`) re-sorts selected-first on
 * every render, which makes a row jump position the instant you tick it. Per
 * the client request (issue 6) selecting an option must NOT reorder the list,
 * so the order is derived purely from price/title and is independent of the
 * current selection.
 */
export function sortOptionRows(
  items: ReadonlyArray<ConfigureOptionDto>,
): ConfigureOptionDto[] {
  return [...items].sort((a, b) => {
    const pa = parseDecimalString(a.price) ?? -Infinity;
    const pb = parseDecimalString(b.price) ?? -Infinity;
    if (pa !== pb) return pb - pa;
    const ta = String(a.itemName ?? a.description ?? a.partNo);
    const tb = String(b.itemName ?? b.description ?? b.partNo);
    return ta.localeCompare(tb);
  });
}

export function optionDisplayTitle(
  item: ConfigureOptionDto,
  isTooling?: boolean,
): string {
  if (isTooling) {
    return String(item.description || item.partNo || "").trim() || item.partNo;
  }
  return String(item.itemName ?? item.description ?? item.partNo).trim();
}
